import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { GitBranch, ChevronRight, ChevronDown, User, Wallet } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const fmt = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function flatten(nodes, level, out, openSet) {
  for (const n of nodes || []) {
    out.push({ ...n, _level: level });
    if (openSet.has(n.id) && (n.children || []).length > 0) {
      flatten(n.children, level + 1, out, openSet);
    }
  }
}

function computeDepth(nodes, depth) {
  if (!nodes || nodes.length === 0) return depth - 1;
  let m = depth;
  for (const n of nodes) {
    const d = computeDepth(n.children || [], depth + 1);
    if (d > m) m = d;
  }
  return m;
}

export default function AffiliateSubTree() {
  const [data, setData] = useState({ roots: [], total_accounts: 0, with_parent: 0 });
  const [loading, setLoading] = useState(true);
  const [openSet, setOpenSet] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/aff-sub-affiliates/tree");
      const d = r.data || { roots: [] };
      setData(d);
      // Open level-0 by default
      const initOpen = new Set((d.roots || []).map((x) => x.id));
      setOpenSet(initOpen);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flatRows = useMemo(() => {
    const out = [];
    flatten(data.roots || [], 0, out, openSet);
    return out;
  }, [data, openSet]);

  const depth = useMemo(() => computeDepth(data.roots || [], 1), [data]);

  const toggle = (id) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="admin-subtree-page">
      <PageHeader
        eyebrow="AFFILIATE"
        title="Sub-Affiliate Tree"
        subtitle="Hierarchical view of every affiliate and their downline."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="rounded-xl p-4 bg-[#0f1a2e] border border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Total Accounts</div>
          <div className="text-2xl font-black text-white mt-1">{data.total_accounts || 0}</div>
        </div>
        <div className="rounded-xl p-4 bg-[#0f1a2e] border border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Root Affiliates</div>
          <div className="text-2xl font-black text-white mt-1">{(data.roots || []).length}</div>
        </div>
        <div className="rounded-xl p-4 bg-[#0f1a2e] border border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">With Parent</div>
          <div className="text-2xl font-black text-emerald-300 mt-1">{data.with_parent || 0}</div>
        </div>
        <div className="rounded-xl p-4 bg-[#0f1a2e] border border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Hierarchy Depth</div>
          <div className="text-2xl font-black text-white mt-1">{depth}</div>
        </div>
      </div>

      <div className="rounded-xl bg-[#0f1a2e] border border-white/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <GitBranch size={14} className="text-emerald-300" />
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Hierarchy</div>
        </div>
        <div className="p-3">
          {loading ? (
            <div className="p-8 text-center text-white/55">Loading…</div>
          ) : (data.roots || []).length === 0 ? (
            <div className="p-8 text-center text-white/55">No affiliate accounts yet.</div>
          ) : flatRows.map((node) => {
            const hasChildren = (node.children || []).length > 0;
            const isOpen = openSet.has(node.id);
            return (
              <div
                key={node.id}
                className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-white/[0.03]"
                style={{ marginLeft: node._level * 20 }}
                data-testid={`tree-node-${node.id}`}
              >
                {hasChildren ? (
                  <button onClick={() => toggle(node.id)} className="text-white/55 hover:text-white">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                ) : (
                  <span style={{ width: 14, display: "inline-block" }} />
                )}
                <User size={13} className="text-emerald-300" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[13px] font-bold">{node.username}</div>
                  <div className="text-white/45 text-[10.5px]">{node.email || ""} · joined {(node.created_at || "").slice(0, 10)}</div>
                </div>
                <code className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 text-[10.5px] font-bold">{node.promo_code || "—"}</code>
                <div className="inline-flex items-center gap-1 text-white/70 text-[11.5px]">
                  <Wallet size={11} /> ৳{fmt(node.wallet_balance)}
                </div>
                <span className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase ${node.status === "active" ? "text-emerald-300 bg-emerald-500/10" : "text-red-300 bg-red-500/10"}`}>
                  {node.status || "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
